/**
 * 模板系统统一类型定义
 * 前后端共享，确保配置一致性
 */

// 基础模板配置接口（前后端通用）
export interface TemplateConfig {
  // 基础信息
  id: string;
  name: string;
  description: string;
  
  // 功能配置
  cardCount?: number;
  
  // 字段配置 - 所有默认值集中定义
  fieldDefaults: {
    title?: string;
    customerName?: string;
    customerQuestion?: string;
    cardInfos?: string[];
    cardPlaceholders?: string[]; // 每张牌的提示文本数组，按位置顺序
    [key: string]: any;
  };
  
  // 字段状态配置 - 前后端自动同步
  disabledFields: string[];
  
  // 特殊配置
  generateOptions?: {
    normal: boolean;
    thinking: boolean;
  };
  
  // 特殊分类配置（用于UI分组显示）
  specialCategory?: string;
  
  // UI配置（仅前端使用）
  ui?: {
    icon?: string; // 图标字符或标识符
    color?: string;
    backgroundColor?: string;
  };

  // 牌阵布局（可选）：用于生成牌阵图的坐标与尺寸配置
  spreadLayout?: SpreadLayoutConfig;
}

export type SpreadLayoutCoordinate = 'normalized' | 'designPixels';
export type SpreadLayoutAnchor = 'top-left' | 'center';

export interface SpreadLayoutConfig {
  coordinate: SpreadLayoutCoordinate;

  background?: { file?: string };

  designSize?: { width: number; height: number };

  cardSize: {
    widthRatio?: number;
    heightRatio?: number;
    widthPx?: number;
    heightPx?: number;
    aspectRatio?: number;
    anchor?: SpreadLayoutAnchor;
  };

  positions: Array<{
    x: number;
    y: number;
    zIndex?: number;
    scale?: number;
  }>;
}

// 前端专用模板接口（扩展基础配置，添加React相关）
export interface FrontendTemplateConfig extends TemplateConfig {
  // React组件相关
  icon: any; // React.ReactNode，前端适配器会处理
}

// 后端专用模板接口（基础配置即可）
export type BackendTemplateConfig = TemplateConfig;

// 字段名称常量（避免硬编码）
export const FIELD_NAMES = {
  TITLE: 'title',
  CUSTOMER_NAME: 'customerName',
  CUSTOMER_QUESTION: 'customerQuestion',
  CARD_COUNT: 'cardCount',
  CARD_INFOS: 'cardInfos'
} as const;

export const TEMPLATE_IDS = {
  FOUR_SEASONS_SPREAD: 'four-seasons-spread'
} as const;

// 生成选项类型
export interface GenerateOptions {
  normal: boolean;
  thinking: boolean;
}

// 模板UI配置类型
export interface TemplateUIConfig {
  icon?: string;
  color?: string;
  backgroundColor?: string;
}

// 辅助类型
export type TemplateId = string;
export type FieldName = string;
export type DisabledFieldsMap = Record<TemplateId, FieldName[]>;
export type DefaultValuesMap = Record<TemplateId, Record<string, any>>;
